/**
 * Structure & miscellaneous executors — insert/delete rows/columns,
 * find_replace, set_conditional_formatting, add_data_validation,
 * set_page_layout, add_comment, get_comments, delete_comment.
 *
 * Each export is a CoreFn: (ctx, args?) => Promise<unknown> | unknown.
 *
 * Office.js references:
 * - Range.insert/delete: https://learn.microsoft.com/en-us/javascript/api/excel/excel.range
 * - ConditionalFormat: https://learn.microsoft.com/en-us/javascript/api/excel/excel.conditionalformat
 * - DataValidation: https://learn.microsoft.com/en-us/javascript/api/excel/excel.datavalidation
 * - PageLayout: https://learn.microsoft.com/en-us/javascript/api/excel/excel.pagelayout
 * - Comment: https://learn.microsoft.com/en-us/javascript/api/excel/excel.comment
 */

/* global Excel */

import {
  getSheet,
  resolveExcelEnum,
  resolveRange,
  resolveSheetAddress,
} from "./utils";

// ---------------------------------------------------------------------------
// insert_rows_columns
// ---------------------------------------------------------------------------

/**
 * Insert rows or columns into a worksheet.
 *
 * Expected args:
 *   sheetName?: string
 *   type:       "rows" | "columns"
 *   start:      number — 1-based Excel row/column number (row 1 = 1, column A = 1)
 *   count?:     number (default 1)
 */
export function insertRowsColumnsCore(
  ctx: Excel.RequestContext,
  args?: Record<string, unknown>
): unknown {
  const sheetName = args?.sheetName as string | undefined;
  const type = args?.type as string | undefined;
  const start = args?.start as number | undefined;
  const count = (args?.count as number) ?? 1;

  if (!type || (type !== "rows" && type !== "columns")) {
    throw new Error(
      "insert_rows_columns requires a `type` argument ('rows' or 'columns')."
    );
  }
  if (start === undefined || start < 1) {
    throw new Error(
      "insert_rows_columns requires a `start` of 1 or greater (1-based Excel row/column number)."
    );
  }

  const sheet = getSheet(ctx, sheetName);

  // `start` is the 1-based Excel row/column number, matching the numbers in
  // A1 notation — no 0↔1 conversion needed.
  let insertedAt: string;
  if (type === "rows") {
    const endRow = start + count - 1;
    insertedAt = `${start}:${endRow}`;
    sheet.getRange(insertedAt).insert(Excel.InsertShiftDirection.down);
  } else {
    const startCol = columnIndexToLetter(start - 1);
    const endCol = columnIndexToLetter(start + count - 2);
    insertedAt = `${startCol}:${endCol}`;
    sheet.getRange(insertedAt).insert(Excel.InsertShiftDirection.right);
  }

  return { success: true, type, start, count, insertedAt };
}

// ---------------------------------------------------------------------------
// delete_rows_columns
// ---------------------------------------------------------------------------

/**
 * Delete rows or columns from a worksheet.
 *
 * Expected args:
 *   sheetName?: string
 *   type:       "rows" | "columns"
 *   start:      number — 1-based Excel row/column number (row 1 = 1, column A = 1)
 *   count?:     number (default 1)
 */
export function deleteRowsColumnsCore(
  ctx: Excel.RequestContext,
  args?: Record<string, unknown>
): unknown {
  const sheetName = args?.sheetName as string | undefined;
  const type = args?.type as string | undefined;
  const start = args?.start as number | undefined;
  const count = (args?.count as number) ?? 1;

  if (!type || (type !== "rows" && type !== "columns")) {
    throw new Error(
      "delete_rows_columns requires a `type` argument ('rows' or 'columns')."
    );
  }
  if (start === undefined || start < 1) {
    throw new Error(
      "delete_rows_columns requires a `start` of 1 or greater (1-based Excel row/column number)."
    );
  }

  const sheet = getSheet(ctx, sheetName);

  // `start` is the 1-based Excel row/column number, matching A1 notation.
  let deletedAt: string;
  if (type === "rows") {
    const endRow = start + count - 1;
    deletedAt = `${start}:${endRow}`;
    sheet.getRange(deletedAt).delete(Excel.DeleteShiftDirection.up);
  } else {
    const startCol = columnIndexToLetter(start - 1);
    const endCol = columnIndexToLetter(start + count - 2);
    deletedAt = `${startCol}:${endCol}`;
    sheet.getRange(deletedAt).delete(Excel.DeleteShiftDirection.left);
  }

  return { success: true, type, start, count, deletedAt };
}

/** Convert zero-based column index to Excel letter (0 → A, 25 → Z, 26 → AA). */
function columnIndexToLetter(index: number): string {
  let result = "";
  let i = index;
  while (i >= 0) {
    result = String.fromCharCode((i % 26) + 65) + result;
    i = Math.floor(i / 26) - 1;
  }
  return result;
}

// ---------------------------------------------------------------------------
// find_replace
// ---------------------------------------------------------------------------

/**
 * Find and optionally replace text within a range.
 *
 * Expected args:
 *   range?:          string — if omitted, searches entire sheet
 *   sheetName?:      string
 *   findText:        string
 *   replaceText?:    string — if omitted, find-only
 *   matchCase?:      boolean (default false)
 *   matchEntireCell?: boolean (default false)
 *
 * Note: Office.js doesn't have a native find/replace API that returns counts
 * the way the desktop app does. We implement this by reading values, searching
 * in-memory, and (if replacing) writing back modified values.
 */
export async function findReplaceCore(
  ctx: Excel.RequestContext,
  args?: Record<string, unknown>
): Promise<unknown> {
  const rangeAddr = args?.range as string | undefined;
  const sheetName = args?.sheetName as string | undefined;
  const findText = args?.findText as string | undefined;
  const replaceText = args?.replaceText as string | undefined;
  const matchCase = args?.matchCase === true;
  const matchEntireCell = args?.matchEntireCell === true;

  if (!findText) {
    throw new Error("find_replace requires a `findText` argument.");
  }

  const range = rangeAddr
    ? resolveRange(ctx, rangeAddr, sheetName).range
    : getSheet(ctx, sheetName).getUsedRange();

  range.load("values,rowCount,columnCount,address");
  await ctx.sync();

  const values = range.values as unknown[][];
  let matchCount = 0;
  let replaceCount = 0;
  const matchLocations: string[] = [];

  const searchStr = matchCase ? findText : findText.toLowerCase();

  for (let r = 0; r < values.length; r++) {
    const row = values[r];
    if (!row) {
      continue;
    }
    for (let c = 0; c < row.length; c++) {
      const cellVal = row[c];
      if (cellVal === null || cellVal === undefined) {
        continue;
      }

      const cellStr = String(cellVal);
      const compareStr = matchCase ? cellStr : cellStr.toLowerCase();

      let isMatch = false;
      if (matchEntireCell) {
        isMatch = compareStr === searchStr;
      } else {
        isMatch = compareStr.includes(searchStr);
      }

      if (isMatch) {
        matchCount++;
        // Build approximate cell address for reporting
        const colLetter = columnIndexToLetter(c);
        // range.address includes sheet name and start address, but for simplicity
        // we just report relative positions
        matchLocations.push(`${colLetter}${r + 1}`);

        if (replaceText !== undefined) {
          if (matchEntireCell) {
            row[c] = replaceText;
          } else {
            // Replace all occurrences in the cell
            const regex = new RegExp(
              escapeRegExp(findText),
              matchCase ? "g" : "gi"
            );
            row[c] = cellStr.replace(regex, replaceText);
          }
          replaceCount++;
        }
      }
    }
  }

  // Write back if we did replacements
  if (replaceText !== undefined && replaceCount > 0) {
    range.values = values;
  }

  return {
    success: true,
    searchedRange: range.address,
    findText,
    replaceText: replaceText ?? null,
    matchCount,
    replaceCount,
    matchLocations: matchLocations.slice(0, 50), // cap for readability
  };
}

function escapeRegExp(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// ---------------------------------------------------------------------------
// set_conditional_formatting
// ---------------------------------------------------------------------------

/**
 * Apply conditional formatting rules to a range.
 *
 * Expected args:
 *   range:           string
 *   sheetName?:      string
 *   ruleType:        "cellValue" | "colorScale" | "dataBar" | "iconSet"
 *   cellValueRule?:  { operator, value1, value2?, format: { fontColor?, fillColor?, bold?, italic? } }
 *   colorScaleRule?: { minimum, midpoint?, maximum }
 *   clearExisting?:  boolean (default false)
 */
export async function setConditionalFormattingCore(
  ctx: Excel.RequestContext,
  args?: Record<string, unknown>
): Promise<unknown> {
  const rangeAddr = args?.range as string | undefined;
  const sheetName = args?.sheetName as string | undefined;
  const ruleType = args?.ruleType as string | undefined;
  const clearExisting = args?.clearExisting === true;

  if (!rangeAddr) {
    throw new Error("set_conditional_formatting requires a `range` argument.");
  }
  if (!ruleType) {
    throw new Error(
      "set_conditional_formatting requires a `ruleType` argument."
    );
  }

  const { range } = resolveRange(ctx, rangeAddr, sheetName);

  if (clearExisting) {
    range.conditionalFormats.clearAll();
  }

  if (ruleType === "cellValue") {
    const rule = args?.cellValueRule as
      | {
          operator: string;
          value1: string | number;
          value2?: string | number;
          format: {
            fontColor?: string;
            fillColor?: string;
            bold?: boolean;
            italic?: boolean;
          };
        }
      | undefined;

    if (!rule) {
      throw new Error(
        "cellValue ruleType requires a `cellValueRule` configuration."
      );
    }

    const operatorMap: Record<string, Excel.ConditionalCellValueOperator> = {
      equalTo: Excel.ConditionalCellValueOperator.equalTo,
      notEqualTo: Excel.ConditionalCellValueOperator.notEqualTo,
      greaterThan: Excel.ConditionalCellValueOperator.greaterThan,
      greaterThanOrEqualTo:
        Excel.ConditionalCellValueOperator.greaterThanOrEqual,
      lessThan: Excel.ConditionalCellValueOperator.lessThan,
      lessThanOrEqualTo: Excel.ConditionalCellValueOperator.lessThanOrEqual,
      between: Excel.ConditionalCellValueOperator.between,
    };

    const operator = operatorMap[rule.operator];
    if (operator === undefined) {
      throw new Error(`Unknown operator "${rule.operator}".`);
    }

    const cf = range.conditionalFormats.add(
      Excel.ConditionalFormatType.cellValue
    );
    const cellValueRule = cf.cellValue;

    const ruleConfig: Excel.ConditionalCellValueRule = {
      operator,
      formula1: String(rule.value1),
    };
    if (rule.value2 !== undefined) {
      ruleConfig.formula2 = String(rule.value2);
    }
    cellValueRule.rule = ruleConfig;

    if (rule.format.fontColor) {
      cellValueRule.format.font.color = rule.format.fontColor;
    }
    if (rule.format.fillColor) {
      cellValueRule.format.fill.color = rule.format.fillColor;
    }
    if (rule.format.bold !== undefined) {
      cellValueRule.format.font.bold = rule.format.bold;
    }
    if (rule.format.italic !== undefined) {
      cellValueRule.format.font.italic = rule.format.italic;
    }
  } else if (ruleType === "colorScale") {
    const rule = args?.colorScaleRule as
      | {
          minimum: { type: string; value?: string | number; color: string };
          midpoint?: { type: string; value?: string | number; color: string };
          maximum: { type: string; value?: string | number; color: string };
        }
      | undefined;

    if (!rule) {
      throw new Error(
        "colorScale ruleType requires a `colorScaleRule` configuration."
      );
    }

    const cf = range.conditionalFormats.add(
      Excel.ConditionalFormatType.colorScale
    );
    const colorScale = cf.colorScale;

    const criteria: Excel.ConditionalColorScaleCriteria = {
      minimum: {
        type: resolveExcelEnum(
          Excel.ConditionalFormatColorCriterionType,
          rule.minimum.type,
          "color criterion type"
        ),
        color: rule.minimum.color,
      },
      maximum: {
        type: resolveExcelEnum(
          Excel.ConditionalFormatColorCriterionType,
          rule.maximum.type,
          "color criterion type"
        ),
        color: rule.maximum.color,
      },
    };

    if (rule.minimum.value !== undefined) {
      criteria.minimum.formula = String(rule.minimum.value);
    }
    if (rule.maximum.value !== undefined) {
      criteria.maximum.formula = String(rule.maximum.value);
    }

    if (rule.midpoint) {
      criteria.midpoint = {
        type: resolveExcelEnum(
          Excel.ConditionalFormatColorCriterionType,
          rule.midpoint.type,
          "color criterion type"
        ),
        color: rule.midpoint.color,
      };
      if (rule.midpoint.value !== undefined) {
        criteria.midpoint.formula = String(rule.midpoint.value);
      }
    }

    colorScale.criteria = criteria;
  } else if (ruleType === "dataBar") {
    range.conditionalFormats.add(Excel.ConditionalFormatType.dataBar);
    // DataBar uses sensible defaults — gradient fill from min to max
  } else if (ruleType === "iconSet") {
    const iconCf = range.conditionalFormats.add(
      Excel.ConditionalFormatType.iconSet
    );
    // Optional iconSetRule lets the model pick the style + direction;
    // otherwise Office.js defaults to 3 traffic lights.
    const iconRule = args?.iconSetRule as
      | { iconSet?: string; reverseIconOrder?: boolean }
      | undefined;
    if (iconRule?.iconSet) {
      iconCf.iconSet.style = resolveExcelEnum(
        Excel.IconSet,
        iconRule.iconSet,
        "iconSet"
      );
    }
    if (iconRule?.reverseIconOrder !== undefined) {
      iconCf.iconSet.reverseIconOrder = iconRule.reverseIconOrder;
    }
  } else {
    throw new Error(
      `Unknown ruleType "${ruleType}". Use cellValue, colorScale, dataBar, or iconSet.`
    );
  }

  return { success: true, range: rangeAddr, ruleType };
}

// ---------------------------------------------------------------------------
// add_data_validation
// ---------------------------------------------------------------------------

/** Excel rejects a literal (comma-joined) list source longer than this. */
const MAX_LIST_SOURCE_CHARS = 255;

/**
 * Add data validation to a range, replacing any validation already on it.
 *
 * Expected args:
 *   range:         string
 *   sheetName?:    string
 *   type:          "list" | "wholeNumber" | "decimal" | "date" | "textLength" | "custom"
 *   listItems?:    string[] — for "list" type
 *   listSource?:   string — for "list" type; a range holding the allowed values
 *   operator?:     string — for numeric/date/textLength types
 *   value1?:       string | number
 *   value2?:       string | number
 *   formula?:      string — for "custom" type
 *   inputMessage?: { title?: string; message: string }
 *   errorAlert?:   { style?: string; title?: string; message: string }
 */
export function addDataValidationCore(
  ctx: Excel.RequestContext,
  args?: Record<string, unknown>
): unknown {
  const rangeAddr = args?.range as string | undefined;
  const sheetName = args?.sheetName as string | undefined;
  const type = args?.type as string | undefined;

  if (!rangeAddr) {
    throw new Error("add_data_validation requires a `range` argument.");
  }
  if (!type) {
    throw new Error("add_data_validation requires a `type` argument.");
  }

  const { range } = resolveRange(ctx, rangeAddr, sheetName);

  // Build the validation rule
  const rule: Record<string, unknown> = {};

  if (type === "list") {
    rule.list = {
      inCellDropDown: true,
      source: resolveListSource(ctx, args),
    };
  } else if (type === "custom") {
    const formula = args?.formula as string | undefined;
    if (!formula) {
      throw new Error(
        "add_data_validation with type 'custom' requires a `formula` argument."
      );
    }
    rule.custom = { formula };
  } else {
    // wholeNumber, decimal, date, textLength
    const operator = args?.operator as string | undefined;
    const value1 = args?.value1;
    const value2 = args?.value2;

    if (!operator) {
      throw new Error(
        `add_data_validation with type '${type}' requires an \`operator\` argument.`
      );
    }

    const typeKeyMap: Record<string, string> = {
      wholeNumber: "wholeNumber",
      decimal: "decimal",
      date: "date",
      textLength: "textLength",
    };

    const typeKey = typeKeyMap[type];
    if (!typeKey) {
      throw new Error(`Unknown validation type "${type}".`);
    }

    const ruleEntry: Record<string, unknown> = {
      operator: resolveExcelEnum(
        Excel.DataValidationOperator,
        operator,
        "operator"
      ),
      formula1: value1 === undefined ? undefined : String(value1),
    };
    if (value2 !== undefined) {
      ruleEntry.formula2 = String(value2);
    }

    rule[typeKey] = ruleEntry;
  }

  // A cell holds one validation rule, and setting `rule` over an existing one
  // fails with InvalidArgument, so re-applying validation must clear first.
  range.dataValidation.clear();
  range.dataValidation.rule = rule as Excel.DataValidationRule;

  // Input message
  const inputMessage = args?.inputMessage as
    | { title?: string; message: string }
    | undefined;
  if (inputMessage) {
    range.dataValidation.prompt = {
      showPrompt: true,
      title: inputMessage.title ?? "",
      message: inputMessage.message,
    };
  }

  // Error alert
  const errorAlert = args?.errorAlert as
    | { style?: string; title?: string; message: string }
    | undefined;
  if (errorAlert) {
    range.dataValidation.errorAlert = {
      showAlert: true,
      style: resolveExcelEnum(
        Excel.DataValidationAlertStyle,
        errorAlert.style ?? "stop",
        "errorAlert style"
      ),
      title: errorAlert.title ?? "",
      message: errorAlert.message,
    };
  }

  return { success: true, range: rangeAddr, validationType: type };
}

function resolveListSource(
  ctx: Excel.RequestContext,
  args?: Record<string, unknown>
): string | Excel.Range {
  const listItems = args?.listItems as string[] | undefined;
  const listSource = args?.listSource as string | undefined;
  const hasItems = listItems !== undefined && listItems.length > 0;

  if (listSource && hasItems) {
    throw new Error(
      "add_data_validation accepts either `listItems` or `listSource`, not both."
    );
  }
  if (listSource) {
    return resolveRange(ctx, listSource).range;
  }
  if (!hasItems) {
    throw new Error(
      "add_data_validation with type 'list' requires `listItems` (the allowed " +
        'values) or `listSource` (a range holding them, e.g. "Lists!A2:A40").'
    );
  }

  const withComma = listItems.find((item) => item.includes(","));
  if (withComma !== undefined) {
    throw new Error(
      `List item "${withComma}" contains a comma, which Excel treats as a ` +
        "separator. Write the values to a range and pass it as `listSource` instead."
    );
  }
  const source = listItems.join(",");
  if (source.length > MAX_LIST_SOURCE_CHARS) {
    throw new Error(
      `The ${listItems.length} list items total ${source.length} characters; ` +
        `Excel caps an inline list at ${MAX_LIST_SOURCE_CHARS}. Write the values ` +
        'to a range (e.g. a column on a "Lists" sheet) and pass it as `listSource`.'
    );
  }
  return source;
}

// ---------------------------------------------------------------------------
// set_page_layout
// ---------------------------------------------------------------------------

/**
 * Configure page layout and print settings.
 *
 * Expected args:
 *   sheetName?:          string
 *   orientation?:        "portrait" | "landscape"
 *   paperSize?:          "letter" | "legal" | "a4" | "a3" | "tabloid"
 *   margins?:            { top?, bottom?, left?, right?, header?, footer? }
 *   printArea?:          string
 *   fitToPage?:          { fitToWidth?, fitToHeight? }
 *   printGridlines?:     boolean
 *   printHeadings?:      boolean
 *   centerHorizontally?: boolean
 *   centerVertically?:   boolean
 */
export function setPageLayoutCore(
  ctx: Excel.RequestContext,
  args?: Record<string, unknown>
): unknown {
  const sheetNameArg = args?.sheetName as string | undefined;
  const printAreaArg = args?.printArea as string | undefined;
  // A multi-area print area ("A1:B5,D1:E5") can't be split into one sheet +
  // address, so only a single area routes the call to its prefixed sheet.
  const target =
    printAreaArg && !printAreaArg.includes(",")
      ? resolveSheetAddress(printAreaArg, sheetNameArg)
      : { address: printAreaArg, sheetName: sheetNameArg };
  const sheet = getSheet(ctx, target.sheetName);
  const layout = sheet.pageLayout;

  // Orientation
  if (args?.orientation) {
    layout.orientation = resolveExcelEnum(
      Excel.PageOrientation,
      args.orientation as string,
      "orientation"
    );
  }

  // Paper size
  if (args?.paperSize) {
    layout.paperSize = resolveExcelEnum(
      Excel.PaperType,
      args.paperSize as string,
      "paperSize"
    );
  }

  // Margins (in inches → points: 1 inch = 72 points)
  const margins = args?.margins as Record<string, number> | undefined;
  if (margins) {
    if (margins.top !== undefined) {
      layout.topMargin = margins.top * 72;
    }
    if (margins.bottom !== undefined) {
      layout.bottomMargin = margins.bottom * 72;
    }
    if (margins.left !== undefined) {
      layout.leftMargin = margins.left * 72;
    }
    if (margins.right !== undefined) {
      layout.rightMargin = margins.right * 72;
    }
    if (margins.header !== undefined) {
      layout.headerMargin = margins.header * 72;
    }
    if (margins.footer !== undefined) {
      layout.footerMargin = margins.footer * 72;
    }
  }

  // Print area ("" clears it)
  if (target.address !== undefined) {
    layout.setPrintArea(target.address);
  }

  // Fit to page — PageLayoutZoomOptions uses `scale`, or we set
  // setPrintTitleRows/Columns for fit-to-page behavior.
  // The zoom property accepts { scale?: number } for percentage zoom.
  // For fit-to-page, we use the dedicated methods on layout.
  const fitToPage = args?.fitToPage as
    | { fitToWidth?: number; fitToHeight?: number }
    | undefined;
  if (fitToPage) {
    // Set zoom to auto (0 scale) so fit-to-page takes effect
    layout.zoom = { scale: 0 } as Excel.PageLayoutZoomOptions;
    // Note: Office.js PageLayout doesn't directly expose fitToWidth/fitToHeight
    // as writable properties. The zoom.scale=0 enables fit-to-page mode, which
    // fits content to the page by default. For precise control, users should
    // use the Excel UI. This sets the closest programmatic equivalent.
  }

  // Print gridlines
  if (args?.printGridlines !== undefined) {
    layout.printGridlines = args.printGridlines as boolean;
  }

  // Print headings
  if (args?.printHeadings !== undefined) {
    layout.printHeadings = args.printHeadings as boolean;
  }

  // Center on page
  if (args?.centerHorizontally !== undefined) {
    layout.centerHorizontally = args.centerHorizontally as boolean;
  }
  if (args?.centerVertically !== undefined) {
    layout.centerVertically = args.centerVertically as boolean;
  }

  return { success: true, sheetName: target.sheetName ?? "(active)" };
}

// ---------------------------------------------------------------------------
// add_comment
// ---------------------------------------------------------------------------

/**
 * Add a comment (threaded note) to a cell.
 * If a comment already exists, adds a reply.
 *
 * Expected args:
 *   cellAddress: string
 *   sheetName?:  string
 *   text:        string
 *   resolved?:   boolean
 */
export async function addCommentCore(
  ctx: Excel.RequestContext,
  args?: Record<string, unknown>
): Promise<unknown> {
  const cellAddress = args?.cellAddress as string | undefined;
  const sheetName = args?.sheetName as string | undefined;
  const text = args?.text as string | undefined;
  const resolved = args?.resolved === true;

  if (!cellAddress) {
    throw new Error("add_comment requires a `cellAddress` argument.");
  }
  if (!text) {
    throw new Error("add_comment requires a `text` argument.");
  }

  const { range: cell } = resolveRange(ctx, cellAddress, sheetName);

  // Check if a comment already exists on this cell
  const existingComment = ctx.workbook.comments.getItemByCell(cell);
  existingComment.load("id");

  // Use try pattern: load and sync, then check if it was found
  try {
    await ctx.sync();
    // Comment exists — add a reply
    existingComment.replies.add(text);
    if (resolved) {
      existingComment.resolved = true;
    }
    return { success: true, cellAddress, action: "reply", resolved };
  } catch {
    // No existing comment — create a new one
    const comment = ctx.workbook.comments.add(cell, text);
    if (resolved) {
      comment.resolved = true;
    }
    return { success: true, cellAddress, action: "created", resolved };
  }
}

// ---------------------------------------------------------------------------
// get_comments
// ---------------------------------------------------------------------------

/**
 * Get all comments from a worksheet.
 *
 * Expected args:
 *   sheetName?: string
 *   range?:     string — optional range filter
 */
export async function getCommentsCore(
  ctx: Excel.RequestContext,
  args?: Record<string, unknown>
): Promise<unknown> {
  const rangeArg = args?.range as string | undefined;
  const target = rangeArg
    ? resolveSheetAddress(rangeArg, args?.sheetName as string | undefined)
    : { address: undefined, sheetName: args?.sheetName as string | undefined };
  const rangeAddr = target.address;

  const sheet = getSheet(ctx, target.sheetName);
  const comments = sheet.comments;
  comments.load(
    "items/content,items/authorName,items/resolved,items/replies/items/content,items/replies/items/authorName"
  );

  await ctx.sync();

  // Get cell addresses for each comment (requires loading the cell reference)
  for (const comment of comments.items) {
    comment.load("id");
    // Access the comment's cell location
    const cellRange = comment.getLocation();
    cellRange.load("address");
  }
  await ctx.sync();

  let results = comments.items.map((comment) => {
    const location = comment.getLocation();
    return {
      content: comment.content,
      authorName: comment.authorName,
      resolved: comment.resolved,
      cellAddress: location.address ?? "unknown",
      replies: comment.replies.items.map((reply) => ({
        content: reply.content,
        authorName: reply.authorName,
      })),
    };
  });

  // Filter by range if specified. Pure-JS bounds comparison so we don't
  // pay an extra ctx.sync() per comment — the comment cell addresses are
  // already loaded above.
  if (rangeAddr) {
    const bounds = parseRangeBounds(rangeAddr);
    if (bounds) {
      results = results.filter((r) => {
        const cell = parseCellAddress(r.cellAddress);
        return cell !== null && isCellInBounds(cell, bounds);
      });
    }
  }

  return {
    success: true,
    commentCount: results.length,
    comments: results,
  };
}

// ---------------------------------------------------------------------------
// Address parsing helpers (used by getCommentsCore range filtering)
// ---------------------------------------------------------------------------

interface CellPosition {
  col: number;
  row: number;
}
interface RangeBounds {
  endCol: number;
  endRow: number;
  startCol: number;
  startRow: number;
}

/** Convert "A" → 0, "Z" → 25, "AA" → 26, "AB" → 27, etc. */
function columnLetterToIndex(letter: string): number {
  let result = 0;
  for (let i = 0; i < letter.length; i++) {
    result = result * 26 + (letter.charCodeAt(i) - 64);
  }
  return result - 1;
}

/**
 * Parse "B5", "Sheet1!B5", or "$B$5" into 0-indexed row/col. Returns
 * null on anything we don't recognize so the filter degrades to "include
 * all" rather than throwing.
 */
function parseCellAddress(addr: string): CellPosition | null {
  const cellPart = addr.includes("!") ? (addr.split("!")[1] ?? addr) : addr;
  const cleaned = cellPart.replace(/\$/g, "");
  const match = /^([A-Z]+)(\d+)$/i.exec(cleaned);
  if (!match || !match[1] || !match[2]) {
    return null;
  }
  return {
    col: columnLetterToIndex(match[1].toUpperCase()),
    row: Number.parseInt(match[2], 10) - 1,
  };
}

/**
 * Parse "B2:D10" or "B5" into normalized row/col bounds. The min/max
 * normalization handles inverted ranges ("D10:B2") that Excel accepts.
 */
function parseRangeBounds(rangeAddr: string): RangeBounds | null {
  const rangePart = rangeAddr.includes("!")
    ? (rangeAddr.split("!")[1] ?? rangeAddr)
    : rangeAddr;

  if (rangePart.includes(":")) {
    const parts = rangePart.split(":");
    if (parts.length !== 2 || !parts[0] || !parts[1]) {
      return null;
    }
    const startCell = parseCellAddress(parts[0]);
    const endCell = parseCellAddress(parts[1]);
    if (!startCell || !endCell) {
      return null;
    }
    return {
      startRow: Math.min(startCell.row, endCell.row),
      startCol: Math.min(startCell.col, endCell.col),
      endRow: Math.max(startCell.row, endCell.row),
      endCol: Math.max(startCell.col, endCell.col),
    };
  }

  const cell = parseCellAddress(rangePart);
  if (!cell) {
    return null;
  }
  return {
    startRow: cell.row,
    startCol: cell.col,
    endRow: cell.row,
    endCol: cell.col,
  };
}

function isCellInBounds(cell: CellPosition, bounds: RangeBounds): boolean {
  return (
    cell.row >= bounds.startRow &&
    cell.row <= bounds.endRow &&
    cell.col >= bounds.startCol &&
    cell.col <= bounds.endCol
  );
}

// ---------------------------------------------------------------------------
// delete_comment
// ---------------------------------------------------------------------------

/**
 * Delete a comment from a specific cell.
 *
 * Expected args:
 *   cellAddress: string
 *   sheetName?:  string
 */
export async function deleteCommentCore(
  ctx: Excel.RequestContext,
  args?: Record<string, unknown>
): Promise<unknown> {
  const cellAddress = args?.cellAddress as string | undefined;
  const sheetName = args?.sheetName as string | undefined;

  if (!cellAddress) {
    throw new Error("delete_comment requires a `cellAddress` argument.");
  }

  const { range: cell } = resolveRange(ctx, cellAddress, sheetName);
  const comment = ctx.workbook.comments.getItemByCell(cell);
  comment.delete();

  return { success: true, cellAddress };
}
